/**
 * React Query hooks for Adapters API
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getApiClient } from "../lib/api";
import type { AdapterType } from "../types/api";

const ADAPTER_KEYS = {
  all: ["adapters"] as const,
  list: (workspaceId: string) => [...ADAPTER_KEYS.all, "list", workspaceId] as const,
  overview: (workspaceId: string) => [...ADAPTER_KEYS.all, "overview", workspaceId] as const,
  status: (workspaceId: string, type: AdapterType) =>
    [...ADAPTER_KEYS.all, "status", workspaceId, type] as const,
};

export function useAdapterOverview(workspaceId: string) {
  return useQuery({
    queryKey: ADAPTER_KEYS.overview(workspaceId),
    queryFn: () => getApiClient().getAdapterOverview(workspaceId),
    staleTime: 30_000,
    enabled: workspaceId.length > 0,
  });
}

export function useAdapters(workspaceId: string) {
  return useQuery({
    queryKey: ADAPTER_KEYS.list(workspaceId),
    queryFn: () => getApiClient().getAdapters(workspaceId),
    staleTime: 1000 * 60 * 2,
    enabled: workspaceId.length > 0,
  });
}

export function useAdapterStatus(workspaceId: string, type: AdapterType) {
  return useQuery({
    queryKey: ADAPTER_KEYS.status(workspaceId, type),
    queryFn: () => getApiClient().getAdapterStatus(type, workspaceId),
    staleTime: 1000 * 30,
    enabled: workspaceId.length > 0,
  });
}

export function useConnectAdapter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workspaceId,
      type,
      config,
      dryRun,
      credentialsRef,
      healthCheckUrl,
    }: {
      workspaceId: string;
      type: AdapterType;
      config?: Record<string, unknown>;
      dryRun?: boolean;
      credentialsRef?: string;
      healthCheckUrl?: string;
    }) =>
      getApiClient().connectAdapter(type, workspaceId, {
        config,
        dryRun,
        credentialsRef,
        healthCheckUrl,
      }),
    onSuccess: async (response, variables) => {
      const isDryRun = response.dryRun === true;
      if (isDryRun) return;

      const { workspaceId, type } = variables;

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ADAPTER_KEYS.list(workspaceId) }),
        queryClient.invalidateQueries({ queryKey: ADAPTER_KEYS.overview(workspaceId) }),
        queryClient.invalidateQueries({ queryKey: ADAPTER_KEYS.status(workspaceId, type) }),
      ]);

      if (response.authUrl && typeof window !== "undefined") {
        sessionStorage.setItem("oauth_return_url", window.location.pathname);
        window.location.href = response.authUrl;
      }
    },
  });
}

export function useDisconnectAdapter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workspaceId,
      type,
      dryRun,
    }: {
      workspaceId: string;
      type: AdapterType;
      dryRun?: boolean;
    }) => getApiClient().disconnectAdapter(type, workspaceId, dryRun),
    onSuccess: async (response, variables) => {
      const isDryRun = response.dryRun === true;
      if (isDryRun) return;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ADAPTER_KEYS.list(variables.workspaceId) }),
        queryClient.invalidateQueries({ queryKey: ADAPTER_KEYS.overview(variables.workspaceId) }),
        queryClient.invalidateQueries({ queryKey: ADAPTER_KEYS.status(variables.workspaceId, variables.type) }),
      ]);
    },
  });
}

export function useAdapterActions(workspaceId: string, type: AdapterType) {
  const connect = useConnectAdapter();
  const disconnect = useDisconnectAdapter();

  return {
    connect: (
      config?: Record<string, unknown>,
      options?: { dryRun?: boolean; credentialsRef?: string; healthCheckUrl?: string },
    ) =>
      connect.mutateAsync({
        workspaceId,
        type,
        config,
        dryRun: options?.dryRun,
        credentialsRef: options?.credentialsRef,
        healthCheckUrl: options?.healthCheckUrl,
      }),
    disconnect: (dryRun?: boolean) => disconnect.mutateAsync({ workspaceId, type, dryRun }),
    isConnecting: connect.isPending,
    isDisconnecting: disconnect.isPending,
    error: connect.error || disconnect.error,
  };
}

export function handleOAuthCallback() {
  if (typeof window === "undefined") return;

  const returnUrl = sessionStorage.getItem("oauth_return_url");
  if (returnUrl) {
    sessionStorage.removeItem("oauth_return_url");
    window.history.replaceState({}, "", returnUrl);
  }
}
